import { HHStack } from '@hinge-health/react-component-library';
import { Skeleton, TableCell, TableRow } from '@mui/material';
import { usePrefersReducedMotion } from '../motion/motion';

// Shared loading skeletons for the wizard's async steps. Skeletons preview the
// shape of incoming content, so a KG round-trip reads as "loading fast" rather
// than "frozen" — the same wait feels snappier than a grey "Loading…" line.
// The wave shimmer is suppressed under reduced motion (falls back to a static
// placeholder block, which still conveys "content is coming").

interface SkeletonLinesProps {
  lines?: number;
}

// Stacked text lines — for list-shaped loading regions (e.g. attached CSPs).
export const SkeletonLines = ({
  lines = 3,
}: SkeletonLinesProps): JSX.Element => {
  const reduced = usePrefersReducedMotion();
  return (
    <HHStack spacing={1} aria-busy="true" aria-live="polite">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={14}
          width={`${88 - i * 14}%`}
          animation={reduced ? false : 'wave'}
        />
      ))}
    </HHStack>
  );
};

interface SkeletonTableRowsProps {
  rows?: number;
  columns: number;
}

// Placeholder rows that match a table's column count — render inside a
// <TableBody> while the row data resolves.
export const SkeletonTableRows = ({
  rows = 3,
  columns,
}: SkeletonTableRowsProps): JSX.Element => {
  const reduced = usePrefersReducedMotion();
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: columns }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton variant="text" animation={reduced ? false : 'wave'} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};
